import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Chip
} from "@mui/material";
import {
  Close,
  Edit,
  Delete,
  PlayArrow,
  CheckCircle,
  Error,
  Schedule
} from "@mui/icons-material";
import { IN8NWorkflow } from "../../../../models/n8n-workflow";
import {
  calculateSuccessRate,
  formatLastExecuted,
  getWorkflowTypeLabel
} from "../../utils/helpers";

interface WorkflowDetailDrawerProps {
  open: boolean;
  workflow: IN8NWorkflow | null;
  onClose: () => void;
  onEdit: (workflow: IN8NWorkflow) => void;
  onDelete: (workflow: IN8NWorkflow) => void;
  onExecute: (workflow: IN8NWorkflow) => void;
}

const WorkflowDetailDrawer: React.FC<WorkflowDetailDrawerProps> = ({
  open,
  workflow,
  onClose,
  onEdit,
  onDelete,
  onExecute
}) => {
  if (!workflow) return null;

  const successRate = calculateSuccessRate(
    workflow.successfulExecutions,
    workflow.totalExecutions
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 400, padding: 3 }
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Workflow Details</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Workflow Info */}
      <Box mb={3}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Name
        </Typography>
        <Typography variant="body1" fontWeight={600} mb={2}>
          {workflow.name}
        </Typography>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Description
        </Typography>
        <Typography variant="body2" mb={2}>
          {workflow.description || "No description"}
        </Typography>

        <Box display="flex" gap={1} mb={2}>
          <Chip
            label={getWorkflowTypeLabel(workflow.type)}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={workflow.status === 'active' ? 'Active' : 'Inactive'}
            size="small"
            color={workflow.status === 'active' ? 'success' : 'default'}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Statistics */}
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        Execution Statistics
      </Typography>

      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          borderRadius: 2,
          padding: 2,
          mb: 2
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            Total Executions
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {workflow.totalExecutions}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <CheckCircle fontSize="small" color="success" />
            <Typography variant="body2" color="text.secondary">
              Successful
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight={600} color="success.main">
            {workflow.successfulExecutions}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Error fontSize="small" color="error" />
            <Typography variant="body2" color="text.secondary">
              Failed
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight={600} color="error.main">
            {workflow.failedExecutions}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            Success Rate
          </Typography>
          <Typography variant="body1" fontWeight={700} color="primary.main">
            {successRate}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          borderRadius: 2,
          padding: 2,
          mb: 3
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Schedule fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            Last Executed
          </Typography>
        </Box>
        <Typography variant="body1" fontWeight={600}>
          {formatLastExecuted(workflow.lastExecutedAt)}
        </Typography>
      </Box>

      {/* Last Error */}
      {workflow.lastError && (
        <Box
          sx={{
            backgroundColor: "#ffebee",
            borderRadius: 2,
            padding: 2,
            mb: 3
          }}
        >
          <Typography variant="subtitle2" color="error" gutterBottom>
            Last Error
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {workflow.lastError}
          </Typography>
        </Box>
      )}

      {/* Actions */}
      <Box display="flex" flexDirection="column" gap={1}>
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={() => onExecute(workflow)}
          fullWidth
        >
          Execute Workflow
        </Button>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => onEdit(workflow)}
          fullWidth
        >
          Edit Workflow
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          onClick={() => onDelete(workflow)}
          fullWidth
        >
          Delete Workflow
        </Button>
      </Box>
    </Drawer>
  );
};

export default WorkflowDetailDrawer;
